import { Alert, Box, Grid, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router';
import CollapsibleInfoAlert from '@/components/common/collapsible-info-alert';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { resolveFlowEntityIdForPlayers, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { useScreenSize } from '@/hooks/use-screen-size';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import { StageConfigurationWidget } from './configuration/stage-configuration-widget';
import { RoomConfigurationWidget } from './configuration/room-configuration-widget';
import { InputsConfigurationWidget } from './configuration/inputs-configuration-widget';
import { BroadcastConfigurationWidget } from './configuration/broadcast-configuration-widget';
import { MediaControlSettingsWidget } from './configuration/media-control-settings-widget';
import { MediaAgentSettingsWidget } from './configuration/media-agent-settings-widget';
import { useConfigurationPlayers } from './configuration/use-configuration-players';
import { useGetAvSourcesQuery } from '@/state/queries/players/use-get-av-sources';
import { usePutAvSourcesMutation } from '@/state/mutations/players/use-put-av-sources';

const PLAYER_PAIRING_URL = import.meta.env.VITE_PLAYER_APP_URL ?? 'https://player.britestage.com';

export default function FlowConfigurationTab() {
  const flow = useFlowStore((s) => s.flow);
  const [searchParams] = useSearchParams();
  const eventSlotId = searchParams.get('eventSlotId');
  const bindingFlowId = flow ? resolveLiveBindingFlowId(flow, eventSlotId ?? undefined) : undefined;
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const t = useTranslations();
  const notification = useNotifications();

  const { playersByType, slotContextRequired, pairEntityType } = useConfigurationPlayers({
    parentId: bindingFlowId ?? '',
    parentType: 'Flow',
    eventSlotId,
    enabled: Boolean(flow && bindingFlowId),
  });

  const { data: avSources } = useGetAvSourcesQuery(
    {
      entityId: bindingFlowId ?? '',
      entityType: 'Flow',
      eventSlotId,
    },
    { enabled: Boolean(flow && bindingFlowId && !slotContextRequired) },
  );

  const { mutateAsync: putAvSources, isPending: isSavingAvSources } = usePutAvSourcesMutation();
  const spaceSetupId = avSources?.origin === 'space' ? (avSources.spaceId ?? '') : '';
  const { playersByType: spacePlayersByType } = useConfigurationPlayers({
    parentId: spaceSetupId,
    parentType: 'Space',
    enabled: Boolean(spaceSetupId),
  });

  if (!flow || !bindingFlowId) return null;

  const stagePlayers = playersByType.get('stagePlayer') ?? [];
  const confidencePlayers = playersByType.get('confidencePlayer') ?? [];
  const houseCamPlayers = playersByType.get('houseCamPlayer') ?? [];
  const roomPlayers = playersByType.get('roomPlayer') ?? [];
  const mediaPlayers = playersByType.get('screenPublisher') ?? [];
  const onlinePlayers = playersByType.get('onlinePlayer') ?? [];
  const spaceOrigin = avSources?.origin === 'space';
  const audienceConfidencePlayers = spaceOrigin
    ? (spacePlayersByType.get('confidencePlayer') ?? [])
    : confidencePlayers;
  const audienceHouseCamPlayers = spaceOrigin ? (spacePlayersByType.get('houseCamPlayer') ?? []) : houseCamPlayers;
  const selectedAudienceMonitorId =
    audienceConfidencePlayers.find((player) => player.id === avSources?.audienceVideoPlayerId)?.id ??
    audienceConfidencePlayers[0]?.id ??
    null;
  const connectedAudienceCamId = audienceHouseCamPlayers.find((player) => player.isConnected)?.id ?? null;
  const liveLocked = avSources?.liveLocked === true || flow.liveContext?.isRunning === true;
  const pairingDisabled = slotContextRequired;
  const spaceManagedDisabled = pairingDisabled || spaceOrigin;

  const pairingInfoAlert = (
    <CollapsibleInfoAlert
      title={t('flows.detail.configuration.pairingInfo.title')}
      description={t('flows.detail.configuration.pairingInfo.body', {
        playerUrl: PLAYER_PAIRING_URL,
      })}
      sx={{ mt: 0 }}
    />
  );

  const leftColumn = (
    <Stack spacing={2}>
      {pairingInfoAlert}
      <StageConfigurationWidget
        stagePlayers={stagePlayers}
        confidencePlayers={audienceConfidencePlayers}
        houseCamPlayers={audienceHouseCamPlayers}
        entityId={bindingFlowId}
        entityType={pairEntityType}
        eventSlotId={eventSlotId}
        liveContext={flow.liveContext}
        disabled={spaceManagedDisabled}
        videoPlayerId={avSources?.videoPlayerId}
        audioPlayerId={avSources?.audioPlayerId}
        audienceMonitorPlayerId={selectedAudienceMonitorId}
        audienceCamPlayerId={connectedAudienceCamId}
      />
      <RoomConfigurationWidget
        roomPlayers={roomPlayers}
        entityId={bindingFlowId}
        entityType={pairEntityType}
        eventSlotId={eventSlotId}
        liveContext={flow.liveContext}
        disabled={pairingDisabled}
        videoPlayerId={avSources?.videoPlayerId}
        audioPlayerId={avSources?.audioPlayerId}
      />
    </Stack>
  );

  const rightColumn = (
    <Stack spacing={2}>
      <InputsConfigurationWidget
        mediaPlayers={mediaPlayers}
        entityId={bindingFlowId}
        entityType={pairEntityType}
        eventSlotId={eventSlotId}
        liveContext={flow.liveContext}
        disabled={pairingDisabled}
        mediaPlayersDisabled={spaceOrigin}
        avSources={avSources}
        audienceCam={{
          confidencePlayers: audienceConfidencePlayers,
          houseCamPlayers: audienceHouseCamPlayers,
        }}
        avSelectDisabled={spaceOrigin || liveLocked || isSavingAvSources}
        audienceSelectDisabled={liveLocked || isSavingAvSources}
        onChangeAvSources={async (next) => {
          try {
            await putAvSources({
              entityId: bindingFlowId,
              entityType: 'Flow',
              eventSlotId,
              videoPlayerId: next.videoPlayerId,
              audioPlayerId: next.audioPlayerId,
              audienceVideoPlayerId: next.audienceVideoPlayerId,
            });
          } catch {
            notification.notifyError(t('flows.detail.configuration.inputs.saveError'));
          }
        }}
        videoPlayerId={avSources?.videoPlayerId}
        audioPlayerId={avSources?.audioPlayerId}
      />
      <BroadcastConfigurationWidget
        onlinePlayers={onlinePlayers}
        entityId={bindingFlowId}
        entityType={pairEntityType}
        eventSlotId={eventSlotId}
        liveContext={flow.liveContext}
        disabled={pairingDisabled}
        videoPlayerId={avSources?.videoPlayerId}
        audioPlayerId={avSources?.audioPlayerId}
      />
      <MediaControlSettingsWidget flow={flow} />
      <MediaAgentSettingsWidget flowId={resolveFlowEntityIdForPlayers(flow)} disabled={liveLocked} />
    </Stack>
  );

  return (
    <Box
      width="100%"
      sx={{
        // Keep the last widget clear of mobile browser chrome / home indicator.
        pb: {
          xs: 'calc(96px + env(safe-area-inset-bottom, 0px))',
          md: 3,
        },
      }}
    >
      {slotContextRequired ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('flows.detail.control.selectScheduleOccurrenceForPlayers')}
        </Typography>
      ) : null}

      {spaceOrigin ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('flows.detail.configuration.spaceSetupInUse')}
        </Alert>
      ) : null}

      {isWideScreen ? (
        <Grid container spacing={2}>
          <Grid size={6}>{leftColumn}</Grid>
          <Grid size={6}>{rightColumn}</Grid>
        </Grid>
      ) : (
        <Stack spacing={2}>
          {leftColumn}
          {rightColumn}
        </Stack>
      )}
    </Box>
  );
}
